import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import { getServiceRequests } from '../services/serviceRequestService';
import { getOrders } from '../services/sparePartsService';
import { useRipple } from '../hooks/useRipple';
import { useMenu } from '../contexts/MenuContext';

export default function Home() {
  const navigate = useNavigate();
  const [serviceRequestsTotal, setServiceRequestsTotal] = useState<number>(0);
  const [ordersTotal, setOrdersTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Hooks
  const { isMenuButtonVisible } = useMenu();
  const { ripples, createRipple } = useRipple<string>();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Paralel olarak her iki API'yi de çağır
        const [serviceRequestsResponse, ordersResponse] = await Promise.all([
          getServiceRequests(1, 1), // Sadece total için, 1 kayıt yeterli
          getOrders(1, 1) // Sadece total için, 1 kayıt yeterli
        ]);

        setServiceRequestsTotal(serviceRequestsResponse.total || 0);
        setOrdersTotal(ordersResponse.total || 0);
      } catch (error) {
        console.error('İstatistikler yüklenirken hata:', error);
        // Hata durumunda 0 değerlerde kalsın
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Mobil durumu takibi
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <PageTransition>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl md:text-3xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Ana Sayfa
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Hızlı Servis Pro'ya hoş geldiniz!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
          {/* Taleplerim Kartı */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div
              onClick={(event) => {
                createRipple(event, 'service-requests', () => {
                  navigate('/service-requests');
                });
              }}
              className={`group relative cursor-pointer border border-blue-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-purple-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 hover:from-blue-100/90 hover:via-indigo-100/70 hover:to-purple-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 backdrop-blur-sm ${isMobile ? '' : 'transition-all duration-500 hover:shadow-xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
            >
              <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
              {/* Ripple Effects */}
              {ripples['service-requests']?.map((ripple) => (
                <motion.div
                  key={ripple.id}
                  className="absolute rounded-full bg-blue-500/25 pointer-events-none"
                  style={{
                    left: ripple.x - (ripple.size || 60) / 2,
                    top: ripple.y - (ripple.size || 60) / 2,
                    width: ripple.size || 60,
                    height: ripple.size || 60,
                  }}
                  initial={{ scale: 0.1, opacity: 0.8 }}
                  animate={{ scale: 1.2, opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              ))}
              
              {/* Enhanced Background Pattern */}
              <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]">
                <div className="absolute -top-2 -right-2 w-24 h-24 bg-blue-500 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-2 -left-2 w-20 h-20 bg-indigo-400 rounded-full blur-xl"></div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute top-3 left-3 w-1.5 h-1.5 bg-blue-400/20 rounded-full"></div>
              <div className="absolute top-5 left-6 w-1 h-1 bg-blue-400/15 rounded-full"></div>
              <div className="absolute bottom-6 right-8 w-1 h-1 bg-blue-400/10 rounded-full"></div>
              
              <div className="relative">
                {/* Header Section */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-105 transition-all duration-300'} ring-2 ring-blue-500/10`}>
                    <div className="w-6 h-6 text-white">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Arrow Icon */}
                  <div className={`text-blue-400 group-hover:text-blue-600 dark:group-hover:text-blue-300 ${isMobile ? '' : 'group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300'} opacity-60 group-hover:opacity-100`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="space-y-2">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-100 ${isMobile ? '' : 'transition-colors duration-300'}">
                        Taleplerim
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed mb-1">
                        Toplam servis talebi
                      </p>
                    </div>
                    
                    {/* Number Display */}
                    <div className="text-right">
                      {loading ? (
                        <div className="space-y-1">
                          <div className="animate-pulse bg-blue-200/60 dark:bg-blue-800/40 h-8 w-16 rounded-lg"></div>
                          <div className="animate-pulse bg-blue-200/40 dark:bg-blue-800/30 h-2 w-12 rounded-full"></div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-2xl font-black text-blue-700 dark:text-blue-300 leading-none mb-0.5">
                            {serviceRequestsTotal.toLocaleString('tr-TR')}
                          </div>
                          <div className="text-xs font-medium text-blue-500/70 dark:text-blue-400/70 mb-1">
                            TOPLAM
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar Effect */}
                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500/20 to-transparent rounded-full"></div>
              </div>
              </div>
            </div>
          </motion.div>

          {/* Siparişlerim Kartı */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div
              onClick={(event) => {
                createRipple(event, 'orders', () => {
                  navigate('/orders');
                });
              }}
              className={`group relative cursor-pointer border border-emerald-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-teal-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 hover:from-emerald-100/90 hover:via-green-100/70 hover:to-teal-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 backdrop-blur-sm ${isMobile ? '' : 'transition-all duration-500 hover:shadow-xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
            >
              <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
              {/* Ripple Effects */}
              {ripples['orders']?.map((ripple) => (
                <motion.div
                  key={ripple.id}
                  className="absolute rounded-full bg-emerald-500/25 pointer-events-none"
                  style={{
                    left: ripple.x - (ripple.size || 60) / 2,
                    top: ripple.y - (ripple.size || 60) / 2,
                    width: ripple.size || 60,
                    height: ripple.size || 60,
                  }}
                  initial={{ scale: 0.1, opacity: 0.8 }}
                  animate={{ scale: 1.2, opacity: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                />
              ))}
              
              {/* Enhanced Background Pattern */}
              <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]">
                <div className="absolute -top-2 -right-2 w-24 h-24 bg-emerald-500 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-2 -left-2 w-20 h-20 bg-green-400 rounded-full blur-xl"></div>
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute top-3 left-3 w-1.5 h-1.5 bg-emerald-400/20 rounded-full"></div>
              <div className="absolute top-5 left-6 w-1 h-1 bg-emerald-400/15 rounded-full"></div>
              <div className="absolute bottom-6 right-8 w-1 h-1 bg-emerald-400/10 rounded-full"></div>
              
              <div className="relative">
                {/* Header Section */}
                <div className="flex items-start justify-between mb-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-emerald-500 via-emerald-600 to-green-700 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-105 transition-all duration-300'} ring-2 ring-emerald-500/10`}>
                    <div className="w-6 h-6 text-white">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                  </div>
                  
                  {/* Arrow Icon */}
                  <div className={`text-emerald-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-300 ${isMobile ? '' : 'group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300'} opacity-60 group-hover:opacity-100`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="space-y-2">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-gray-900 dark:group-hover:text-gray-100 ${isMobile ? '' : 'transition-colors duration-300'}">
                        Siparişlerim
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed mb-1">
                        Toplam parça siparişi
                      </p>
                    </div>
                    
                    {/* Number Display */}
                    <div className="text-right">
                      {loading ? (
                        <div className="space-y-1">
                          <div className="animate-pulse bg-emerald-200/60 dark:bg-emerald-800/40 h-8 w-16 rounded-lg"></div>
                          <div className="animate-pulse bg-emerald-200/40 dark:bg-emerald-800/30 h-2 w-12 rounded-full"></div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-2xl font-black text-emerald-700 dark:text-emerald-300 leading-none mb-0.5">
                            {ordersTotal.toLocaleString('tr-TR')}
                          </div>
                          <div className="text-xs font-medium text-emerald-500/70 dark:text-emerald-400/70 mb-1">
                            TOPLAM
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar Effect */}
                <div className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent rounded-full"></div>
              </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Hızlı Erişim
            </h2>
            <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Hesabım */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'account');
                  setTimeout(() => {
                    navigate('/account');
                  }, 150);
                }}
                className={`group relative cursor-pointer border border-blue-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-purple-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-blue-100/90 hover:via-indigo-100/70 hover:to-purple-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['account']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-blue-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Hesabım
                      </h3>
                      <div className={`flex-shrink-0 text-blue-500 group-hover:text-blue-700 dark:group-hover:text-blue-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Hesabınızı yönetin</span>
                      <span className="hidden sm:inline">Hesap bilgilerinizi ve güvenlik ayarlarınızı yönetin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>

            {/* Yedek Parça */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'spare-parts');
                  setTimeout(() => {
                    navigate('/spare-parts');
                  }, 150);
                }}
                className={`group relative cursor-pointer border border-emerald-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-emerald-50/80 via-green-50/60 to-teal-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-emerald-100/90 hover:via-green-100/70 hover:to-teal-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['spare-parts']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-emerald-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Yedek Parça
                      </h3>
                      <div className={`flex-shrink-0 text-emerald-500 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Parça siparişi verin</span>
                      <span className="hidden sm:inline">Yedek parça siparişi verin ve stok durumunu kontrol edin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>            

            {/* Garanti Başlatma */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'warranty-start');
                  setTimeout(() => {
                    navigate('/warranty-start');
                  }, 150);
                }}
                className={`group relative cursor-pointer border border-violet-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-violet-50/80 via-purple-50/60 to-pink-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-violet-100/90 hover:via-purple-100/70 hover:to-pink-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['warranty-start']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-violet-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-violet-700 dark:group-hover:text-violet-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        <span className="sm:hidden">Garanti Girişi</span>
                        <span className="hidden sm:inline">Garanti Başlatma</span>
                      </h3>
                      <div className={`flex-shrink-0 text-violet-500 group-hover:text-violet-700 dark:group-hover:text-violet-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Garanti başlatın</span>
                      <span className="hidden sm:inline">Garanti başlatın ve garanti işlemlerinizi yönetin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>

            {/* Servis Talebi */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'service-request');
                  setTimeout(() => {
                    navigate('/service-request');
                  }, 150);
                }}
                className={`group relative cursor-pointer border border-orange-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-orange-100/90 hover:via-amber-100/70 hover:to-yellow-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['service-request']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-orange-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Servis Talebi
                      </h3>
                      <div className={`flex-shrink-0 text-orange-500 group-hover:text-orange-700 dark:group-hover:text-orange-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Talep oluşturun</span>
                      <span className="hidden sm:inline">Yeni servis talebi oluşturun ve taleplerinizi takip edin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
          </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
} 